// frontend/src/Blog.js
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Box, Chip, CircularProgress } from '@mui/material';
import moment from 'jalali-moment';
import apiClient from './api';
import ElderlyIcon from '@mui/icons-material/Elderly';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { brand, glass } from './theme';

const CATEGORY_META = {
    elderly: { label: 'مراقبت سالمند', icon: ElderlyIcon, gradient: `linear-gradient(135deg, ${brand.primary}, ${brand.primaryDeep})` },
    child: { label: 'مراقبت کودک', icon: ChildCareIcon, gradient: `linear-gradient(135deg, ${brand.accent}, ${brand.accentDeep})` },
    patient: { label: 'مراقبت بیمار', icon: LocalHospitalIcon, gradient: `linear-gradient(135deg, #6E8F87, ${brand.primaryDeep})` },
    guide: { label: 'راهنمای خانواده', icon: MenuBookIcon, gradient: `linear-gradient(135deg, ${brand.accentDeep}, #6B4A1E)` },
};

function ArticleCard({ a }) {
    const meta = CATEGORY_META[a.category] || CATEGORY_META.guide;
    const Icon = meta.icon;
    return (
        <Box
            component={RouterLink}
            to={`/blog/${a.slug}`}
            sx={{
                ...glass.card, textDecoration: 'none', color: 'inherit', overflow: 'hidden',
                display: 'flex', flexDirection: 'column', transition: 'transform .2s ease, box-shadow .2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 20px 50px rgba(15,51,43,0.18)' },
            }}
        >
            <Box sx={{
                height: 108, background: meta.gradient, display: 'flex', alignItems: 'center',
                justifyContent: 'center', position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)', top: -60, insetInlineEnd: -40 }} />
                <Icon sx={{ fontSize: 46, color: 'rgba(255,255,255,0.92)' }} />
            </Box>
            <Box sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Chip size="small" label={meta.label} sx={{ alignSelf: 'flex-start', bgcolor: brand.primaryTint, color: brand.primary, fontWeight: 700, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.4 }}>{a.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, flexGrow: 1 }}>{a.excerpt}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2, color: 'text.disabled' }}>
                    <ScheduleIcon sx={{ fontSize: 15 }} />
                    <Typography variant="caption">{moment(a.published_at).locale('fa').format('D MMMM YYYY')} · {a.reading_minutes} دقیقه مطالعه</Typography>
                </Box>
            </Box>
        </Box>
    );
}

function Blog() {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState(null);

    useEffect(() => {
        document.title = 'مقالات الو یارا — راهنمای مراقبت در منزل';
        apiClient.get('/api/articles/')
            .then((res) => setArticles(Array.isArray(res.data) ? res.data : []))
            .catch(() => setArticles([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = category ? articles.filter((a) => a.category === category) : articles;
    const presentCategories = [...new Set(articles.map((a) => a.category))];

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%' }}>
            <Box sx={{ background: `linear-gradient(180deg, ${brand.primaryDeep} 0%, ${brand.primary} 140%)`, color: brand.heroText, position: 'relative', overflow: 'hidden', pb: 8, pt: { xs: 6, md: 8 } }}>
                <Box sx={{ position: 'absolute', width: 380, height: 380, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)', top: -160, insetInlineStart: -100 }} />
                <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(222,156,76,0.15)', bottom: -120, insetInlineEnd: -60 }} />
                <Container maxWidth="md" sx={{ position: 'relative' }}>
                    <Typography component="span" sx={{ display: 'block', fontSize: '.8rem', fontWeight: 700, color: brand.accent, mb: 1.5 }}>
                        مقالات
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5 }}>راهنمای خانواده برای مراقبت در منزل</Typography>
                    <Typography sx={{ color: 'rgba(245,243,234,0.8)', maxWidth: '60ch' }}>
                        نکات عملی و قابل‌اعتماد درباره‌ی مراقبت از سالمند، کودک و بیمار در منزل — نوشته‌شده توسط تیم الو یارا.
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ mt: -5, pb: { xs: 6, md: 9 } }}>
                {presentCategories.length > 1 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                        <Chip
                            label="همه" clickable onClick={() => setCategory(null)}
                            color={!category ? 'primary' : 'default'}
                            variant={!category ? 'filled' : 'outlined'}
                        />
                        {presentCategories.map((c) => (
                            <Chip
                                key={c} label={CATEGORY_META[c]?.label || c} clickable
                                onClick={() => setCategory(c)}
                                color={category === c ? 'primary' : 'default'}
                                variant={category === c ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Box>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ ...glass.card, p: 5, textAlign: 'center' }}>
                        <Typography color="text.secondary">به‌زودی مقاله‌ای اینجا منتشر می‌شود.</Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                        {filtered.map((a) => <ArticleCard key={a.id} a={a} />)}
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default Blog;
